import {
  CloseCircleOutlined,
  CloseOutlined,
  FileOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { message, Modal, Upload } from 'antd';
import Dragger from 'antd/es/upload/Dragger';
import { useTranslation } from 'react-i18next';
import type { FC } from 'react';
import { useState, useEffect } from 'react';
import { useUploadDocuments } from '@/hooks/useDocuments';

const FILE_LIMIT = 100;

interface ModalAddFilesProps {
  node: any;
  onLoadData: (node: any, leaves: any[]) => void;
  open: boolean;
  onCancel: () => void;
}

interface FileWithPath {
  file: File;
  path: string;
  uid: string;
}

const ModalAddFiles: FC<ModalAddFilesProps> = ({
  node,
  onLoadData,
  open,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [fileList, setFileList] = useState<any[]>([]);
  const [filesWithPaths, setFilesWithPaths] = useState<FileWithPath[]>([]);
  const uploadDocumentsMutation = useUploadDocuments();
  const [isRefetching, setIsRefetching] = useState(false);
  const isLoading = uploadDocumentsMutation.isPending || isRefetching;

  const resetFiles = () => {
    setFileList([]);
    setFilesWithPaths([]);
  };

  useEffect(() => {
    if (open) {
      setFileList([]);
      setFilesWithPaths([]);
    }
  }, [open]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isLoading) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isLoading]);

  const normalizePath = (path: string): string => {
    if (!path) return '';
    const parts = path
      .split('/')
      .filter(part => part && part !== '.' && part !== '..');
    return parts.join('/');
  };

  const processDirectoryEntry = async (
    entry: FileSystemDirectoryEntry,
    path: string = '',
  ): Promise<FileWithPath[]> => {
    const files: FileWithPath[] = [];

    return new Promise(resolve => {
      const reader = entry.createReader();
      const readEntries = () => {
        reader.readEntries(async entries => {
          if (entries.length === 0) {
            resolve(files);
            return;
          }

          for (const entry of entries) {
            if (
              entry.name.startsWith('.') ||
              entry.name === '.' ||
              entry.name === '..'
            ) {
              continue;
            }

            const normalizedPath = normalizePath(path);
            const fullPath = normalizedPath
              ? `${normalizedPath}/${entry.name}`
              : entry.name;

            if (entry.isFile) {
              const fileEntry = entry as FileSystemFileEntry;
              await new Promise<void>(fileResolve => {
                fileEntry.file((file: File) => {
                  files.push({
                    file,
                    path: fullPath,
                    uid: `${Date.now()}-${Math.random()}-${file.name}`,
                  });
                  fileResolve();
                });
              });
            } else if (entry.isDirectory) {
              const dirEntry = entry as FileSystemDirectoryEntry;
              const subFiles = await processDirectoryEntry(dirEntry, fullPath);
              files.push(...subFiles);
            }
          }

          readEntries();
        });
      };

      readEntries();
    });
  };

  const handleFileChange = async (info: any) => {
    setFileList(info.fileList);

    const updatedFilesWithPaths: FileWithPath[] = info.fileList
      .map((fileItem: any) => {
        const fileObj = fileItem.originFileObj || fileItem;
        if (fileObj instanceof File) {
          const existing = filesWithPaths.find(f => f.uid === fileItem.uid);
          if (existing) {
            return existing;
          }
          return {
            file: fileObj,
            path: fileItem.name || fileObj.name,
            uid: fileItem.uid,
          };
        }
        return null;
      })
      .filter((item: FileWithPath | null) => item !== null) as FileWithPath[];

    setFilesWithPaths(updatedFilesWithPaths);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    const items = Array.from(e.dataTransfer.items);

    let hasDirectory = false;
    for (const item of items) {
      if (item.webkitGetAsEntry) {
        const entry = item.webkitGetAsEntry();
        if (entry?.isDirectory) {
          hasDirectory = true;
          break;
        }
      }
    }

    if (!hasDirectory) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();

    try {
      const newFilesWithPaths: FileWithPath[] = [];

      for (const item of items) {
        if (item.webkitGetAsEntry) {
          const entry = item.webkitGetAsEntry();

          if (entry?.isDirectory) {
            const rootFolderName = entry.name;
            if (
              rootFolderName &&
              rootFolderName !== '.' &&
              rootFolderName !== '..' &&
              !rootFolderName.startsWith('.')
            ) {
              const dirFiles = await processDirectoryEntry(
                entry as FileSystemDirectoryEntry,
                rootFolderName,
              );
              newFilesWithPaths.push(...dirFiles);
            }
          } else if (entry?.isFile) {
            const fileEntry = entry as FileSystemFileEntry;
            await new Promise<void>(resolve => {
              fileEntry.file((file: File) => {
                const normalizedPath = normalizePath(file.name);
                newFilesWithPaths.push({
                  file,
                  path: normalizedPath || file.name,
                  uid: `${Date.now()}-${Math.random()}-${file.name}`,
                });
                resolve();
              });
            });
          }
        } else {
          const file = item.getAsFile();
          if (file) {
            const normalizedPath = normalizePath(file.name);
            newFilesWithPaths.push({
              file,
              path: normalizedPath || file.name,
              uid: `${Date.now()}-${Math.random()}-${file.name}`,
            });
          }
        }
      }

      const currentFileCount = filesWithPaths.length;
      const newFileCount = currentFileCount + newFilesWithPaths.length;

      if (newFileCount > FILE_LIMIT) {
        message.error(
          t('home.documents.modals.addFile.limitError', { n: FILE_LIMIT }),
        );
        return;
      }

      const updatedFilesWithPaths = [...filesWithPaths, ...newFilesWithPaths];
      setFilesWithPaths(updatedFilesWithPaths);

      const updatedFileList = updatedFilesWithPaths.map((item, index) => ({
        uid: item.uid,
        name: item.path,
        status: 'done' as const,
        originFileObj: item.file,
      }));

      setFileList(updatedFileList);
    } catch (error) {
      message.error(t('home.documents.modals.addFile.error'));
    }
  };

  const validateFiles = (): boolean => {
    const invalidFiles: string[] = [];

    if (filesWithPaths.length > 0) {
      filesWithPaths.forEach(item => {
        if (!isFileTypeAllowed(item.file)) {
          invalidFiles.push(item.path);
        }
      });
    } else {
      fileList.forEach((file: any) => {
        const fileObj = file.originFileObj || file;
        if (fileObj instanceof File && !isFileTypeAllowed(fileObj)) {
          invalidFiles.push(file.name || fileObj.name);
        }
      });
    }

    if (invalidFiles.length > 0) {
      const errorMessage = `${invalidFiles.length} invalid file(s): ${invalidFiles.slice(0, 3).join(', ')}${invalidFiles.length > 3 ? '...' : ''}. Only PDF, DOCX, and TXT files are allowed.`;
      message.error(errorMessage);
      return false;
    }

    return true;
  };

  const handleUpload = async () => {
    if (!node?.id) {
      message.error(t('home.documents.modals.addFile.error'));
      return;
    }

    if (fileList.length === 0 || fileList.length > FILE_LIMIT) {
      message.error(
        t('home.documents.modals.addFile.limitError', { n: FILE_LIMIT }),
      );
      setFileList([]);
      setFilesWithPaths([]);
      return;
    }

    if (!validateFiles()) {
      setFileList([]);
      setFilesWithPaths([]);
      return;
    }

    const formData = new FormData();

    if (filesWithPaths.length > 0) {
      filesWithPaths.forEach(item => {
        const normalizedPath = normalizePath(item.path);
        formData.append('files[]', item.file);
        formData.append('filesPath[]', normalizedPath || item.file.name);
      });
    } else {
      fileList.forEach((file: any) => {
        const fileObj = file.originFileObj || file;
        if (fileObj instanceof File) {
          const fileName = file.name || fileObj.name;
          const normalizedPath = normalizePath(fileName);
          formData.append('files[]', fileObj);
          formData.append('filesPath[]', normalizedPath || fileName);
        }
      });
    }

    try {
      const { success, data, error } =
        await uploadDocumentsMutation.mutateAsync({
          params: { parent_id: node?.id },
          data: formData,
        });
      if (!success) {
        const errorMessage = error?.includes('DOCX')
          ? t('home.documents.modals.addFile.docxConversionError')
          : error || t('home.documents.modals.addFile.error');
        message.error(errorMessage);
        return;
      }

      message.success(t('home.documents.modals.addFile.success'));

      setFileList([]);
      setFilesWithPaths([]);

      setIsRefetching(true);
      try {
        await onLoadData(node, data);
      } finally {
        setIsRefetching(false);
      }

      onCancel();
    } catch (err) {
      message.error(t('home.documents.modals.addFile.error'));
    }
  };

  const customIconRender = (file: any) => {
    if (file.status === 'error') {
      return <CloseCircleOutlined style={{ color: 'red' }} />;
    }
    return <FileOutlined style={{ fontSize: '30px' }} />;
  };

  const isFileTypeAllowed = (file: File): boolean => {
    const allowedMimeTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
    ];

    const allowedExtensions = ['.pdf', '.docx', '.txt'];
    const fileName = file.name.toLowerCase();

    if (file.type && allowedMimeTypes.includes(file.type)) {
      return true;
    }

    return allowedExtensions.some(ext => fileName.endsWith(ext));
  };

  const beforeUpload = (file: any) => {
    return false;
  };

  const props = {
    name: 'file',
    multiple: true,
    fileList: fileList,
    beforeUpload: beforeUpload,
    iconRender: customIconRender,
    onRemove(file: any) {
      setFileList((prevFileList: any) =>
        prevFileList.filter((item: any) => item.uid !== file.uid),
      );
      setFilesWithPaths((prevFiles: FileWithPath[]) =>
        prevFiles.filter((item: FileWithPath) => item.uid !== file.uid),
      );
    },
  };

  return (
    <div className="modal-add-attchments">
      <Modal
        title={t('home.documents.modals.addFile.title')}
        open={open}
        onOk={handleUpload}
        destroyOnClose
        onCancel={() => {
          if (isLoading) {
            message.warning(
              t('home.chat.attachments.files.uploadInProgressWarning'),
            );
            return;
          }
          resetFiles();
          onCancel();
        }}
        closeIcon={
          <div
            onClick={e => {
              if (isLoading) {
                e.preventDefault();
                e.stopPropagation();
                message.warning(
                  t('home.chat.attachments.files.uploadInProgressWarning'),
                );
              }
            }}
          >
            <CloseOutlined />
          </div>
        }
        cancelText={t('home.documents.modals.addFile.cancel')}
        okText={t('home.documents.modals.addFile.confirm')}
        confirmLoading={isLoading}
        okType="primary"
        width={'40%'}
        maskClosable={!isLoading}
        keyboard={!isLoading}
      >
        <div style={{ padding: '20px' }}>
          <div onDrop={handleDrop} onDragOver={handleDragOver}>
            <Dragger
              {...props}
              style={{ height: '150px' }}
              onChange={handleFileChange}
            >
              <div style={{ display: 'flex', height: '100%', gap: 20 }}>
                <UploadOutlined
                  style={{ fontSize: '40px', color: '#00000073' }}
                />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                >
                  <p>
                    {t('home.documents.modals.addFile.dragNdrop')}{' '}
                    <a>{t('home.documents.modals.addFile.chooseFile')}</a>
                  </p>
                  <br />
                  <p>
                    ・
                    {t('home.documents.modals.addFile.rule1', {
                      n: FILE_LIMIT,
                    })}
                  </p>
                  <p>・{t('home.documents.modals.addFile.rule2')}</p>
                </div>
              </div>
            </Dragger>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ModalAddFiles;
